import { Component, computed, Inject, type OnInit, signal } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  MultiSelectFilterComponent,
  MultiSelectOption,
} from "@components/filters/multi-select-filter/multi-select-filter.component";
import { IApiResponse } from "@models/api-response.model";
import { RequestService } from "@services/request/request.service";

interface VendorEmailDialogData {
  countryOptions: MultiSelectOption<string>[];
}

// Gmail compose URLs have a length cap; beyond this we copy to clipboard instead of using ?bcc.
const MAX_GMAIL_URL_LEN = 1900;
// authuser selects the hello@fishyhub.com account by email (not positional); tf=cm opens compose.
const GMAIL_BASE = "https://mail.google.com/mail/?fs=1&tf=cm&authuser=hello@fishyhub.com";

/**
 * Vendor "Send Email" dialog: filter vendors by country (+ optionally category and
 * subcategory), then open Gmail web compose with the matching vendors' emails in BCC.
 * Many recipients exceed Gmail's URL limit, so we copy them to the clipboard and open a
 * blank compose to paste into BCC. Never sends anything.
 */
@Component({
  selector: "fh-vendor-email-dialog",
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MultiSelectFilterComponent,
  ],
  template: `
    <h2 mat-dialog-title>Send Email to Vendors</h2>
    <mat-dialog-content>
      <p style="margin: 0 0 12px; color: rgba(0,0,0,0.6);">
        Filter the vendors, then we'll open Gmail with their addresses in BCC. With many
        vendors the emails are copied to your clipboard to paste into BCC.
      </p>

      <fh-multi-select-filter
        [options]="countryOptions2"
        label="Countries"
        placeholder="Select countries"
        (selectionChange)="onCountriesChange($event)"
      />

      <div style="margin-top: 12px;">
        <fh-multi-select-filter
          [options]="categoryOptions()"
          label="Categories (optional)"
          placeholder="Select categories"
          (selectionChange)="onCategoriesChange($event)"
        />
      </div>

      <div style="margin-top: 12px;">
        <fh-multi-select-filter
          [options]="subcategoryOptions()"
          label="Subcategories (optional)"
          placeholder="Select subcategories"
          (selectionChange)="onSubcategoriesChange($event)"
        />
      </div>

      @if (loading()) {
        <p style="margin-top: 12px;">Loading vendor emails…</p>
      } @else if (selectedCountries().length > 0) {
        <p style="margin-top: 12px;">{{ emails().length }} vendor email(s) found.</p>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Cancel</button>
      <button
        mat-raised-button
        color="primary"
        [disabled]="loading() || emails().length === 0"
        (click)="openEmail()"
      >
        Open Gmail
      </button>
    </mat-dialog-actions>
  `,
})
export class VendorEmailDialogComponent implements OnInit {
  selectedCountries = signal<string[]>([]);
  selectedCategories = signal<number[]>([]);
  selectedSubcategories = signal<number[]>([]);

  categoryOptions = signal<MultiSelectOption<number>[]>([]);
  private allSubcategories = signal<{ id: number; category_name: string; parent_id: number }[]>([]);

  emails = signal<string[]>([]);
  loading = signal<boolean>(false);

  // Subcategories shown are scoped to the selected categories (if any).
  subcategoryOptions = computed<MultiSelectOption<number>[]>(() => {
    const cats = this.selectedCategories();
    const list = cats.length
      ? this.allSubcategories().filter((s) => cats.includes(s.parent_id))
      : this.allSubcategories();
    return list.map((s) => ({ value: s.id, label: s.category_name }));
  });

  // Injected dialog data. Assigned in the constructor body (not a parameter property) so the
  // linter sees the param as used and doesn't rename it.
  readonly data: VendorEmailDialogData;

  get countryOptions2(): MultiSelectOption<string>[] {
    return this.data?.countryOptions ?? [];
  }

  constructor(
    private dialogRef: MatDialogRef<VendorEmailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: VendorEmailDialogData,
    private requestService: RequestService,
    private snackBar: MatSnackBar
  ) {
    this.data = data;
  }

  ngOnInit(): void {
    // Load category + subcategory options.
    this.requestService
      .get<IApiResponse<{ list?: { id: number; category_name: string }[] }>>(
        "/api/v1/count_category_list"
      )
      .subscribe({
        next: (res) => {
          const list = (res?.data as { list?: { id: number; category_name: string }[] })?.list ?? [];
          this.categoryOptions.set(list.map((c) => ({ value: c.id, label: c.category_name })));
        },
        error: () => this.categoryOptions.set([]),
      });

    this.requestService
      .get<IApiResponse<{ id: number; category_name: string; parent_id: number }[]>>(
        "/api/v1/subcategory_list"
      )
      .subscribe({
        next: (res) => this.allSubcategories.set((res?.data as any) ?? []),
        error: () => this.allSubcategories.set([]),
      });
  }

  onCountriesChange(values: string[]): void {
    this.selectedCountries.set(values ?? []);
    this.reloadEmails();
  }

  onCategoriesChange(values: number[]): void {
    this.selectedCategories.set(values ?? []);
    // Drop any selected subcategories that no longer belong to the chosen categories.
    if (this.selectedCategories().length) {
      const allowed = new Set(this.subcategoryOptions().map((o) => o.value));
      this.selectedSubcategories.set(this.selectedSubcategories().filter((id) => allowed.has(id)));
    }
    this.reloadEmails();
  }

  onSubcategoriesChange(values: number[]): void {
    this.selectedSubcategories.set(values ?? []);
    this.reloadEmails();
  }

  // Preload emails so "Open Gmail" acts synchronously (needed for the clipboard write).
  private reloadEmails(): void {
    this.emails.set([]);
    const countries = this.selectedCountries();
    if (!countries.length) return;

    const params = new URLSearchParams();
    params.set("iso2", countries.join(","));
    if (this.selectedCategories().length) {
      params.set("categoryId", this.selectedCategories().join(","));
    }
    if (this.selectedSubcategories().length) {
      params.set("subcategoryId", this.selectedSubcategories().join(","));
    }

    this.loading.set(true);
    this.requestService
      .get<IApiResponse<{ emails: string[] }>>(`/api/v2/vendor/emails?${params.toString()}`)
      .subscribe({
        next: (res) => {
          this.loading.set(false);
          this.emails.set((res?.data as { emails?: string[] })?.emails ?? []);
        },
        error: () => {
          this.loading.set(false);
          this.emails.set([]);
          this.snackBar.open("Failed to fetch vendor emails", "Close", { duration: 3000 });
        },
      });
  }

  openEmail(): void {
    const emails = this.emails();
    if (!emails.length) {
      this.snackBar.open("No vendor emails found for the selected filters", "Close", {
        duration: 4000,
      });
      return;
    }

    const withBcc = `${GMAIL_BASE}&bcc=${encodeURIComponent(emails.join(","))}`;
    if (withBcc.length <= MAX_GMAIL_URL_LEN) {
      window.open(withBcc, "_blank", "noopener");
    } else {
      try {
        void navigator.clipboard?.writeText(emails.join(", "));
      } catch {
        // clipboard unavailable — non-fatal
      }
      window.open(GMAIL_BASE, "_blank", "noopener");
      this.snackBar.open(
        `${emails.length} vendor emails copied — paste them into the BCC field`,
        "Close",
        { duration: 7000 }
      );
    }
    this.dialogRef.close();
  }
}
